// src/pages/Home.js
import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import site from "../../../config/site";
import useIsMobile from "../../../hooks/useIsMobile";
import useCollectionImages from "../../../hooks/useCollectionImages";
import MobileCarousel from "../mobilecarousel/MobileCarousel";
import MasonryGrid from "../masonrygrid/MasonryGrid";
import Lightbox from "../lightbox/Lightbox";
import Wordmark from "../wordmark/Wordmark";


// ---------------------------------------------------------------------------
// Session‑stable shuffle (same seed for the entire visit).
// ---------------------------------------------------------------------------
function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function sessionSeed() {
  const KEY = "photolevs:shuffle-seed";
  try {
    let s = sessionStorage.getItem(KEY);
    if (!s) {
      s = String((Math.random() * 2 ** 31) | 0);
      sessionStorage.setItem(KEY, s);
    }
    return Number(s);
  } catch {
    return 42;
  }
}

function stableShuffle(items) {
  const rand = mulberry32(sessionSeed());
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export default function Home() {
  const isMobile = useIsMobile();
  const { images, loading, error } = useCollectionImages(site.featuredFolder);
  const [selected, setSelected] = useState(null);

  // Shuffle only when `images` array changes (once per session)
  const items = useMemo(
    () => (site.shuffleHome ? stableShuffle(images) : images),
    [images]
  );

  return (
    <div className="page">
      <Helmet>
        <title>photoLevs</title>
        <meta name="description" content={site.tagline} />
        <meta property="og:title" content="photoLevs" />
        {items[0] && <meta property="og:image" content={items[0].url} />}
      </Helmet>

      {/* Loading state */}
      {loading && (
        <div className="status">
          <Wordmark text="loading" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="status">
          <p className="prose">
            The gallery couldn’t load. Check your connection and refresh the
            page.
          </p>
        </div>
      )}

      {/* Empty state (no images uploaded yet) */}
      {!loading && !error && items.length === 0 && (
        <div className="status">
          <p className="prose">
            No photographs yet. Upload images to the “{site.featuredFolder}”
            folder in Firebase Storage and they will appear here.
          </p>
        </div>
      )}

      {/* Gallery */}
      {!loading && items.length > 0 && (
        <>
          {isMobile ? (
            <MobileCarousel items={items} />
          ) : (
            <MasonryGrid items={items} onSelect={setSelected} />
          )}

          {selected !== null && (
            <Lightbox
              items={items}
              index={selected}
              onClose={() => setSelected(null)}
              onNavigate={setSelected}
            />
          )}
        </>
      )}
    </div>
  );
}